const GA_MEASUREMENT_ID = "G-WPWN901HNC";
const META_PIXEL_ID = "1580375547157170";
const PRODUCTION_VAULT_HOST = "vault.familysafe.co.uk";
const SIGN_UP_EVENT = "sign_up_free";
const SIGN_UP_STORAGE_PREFIX = "familysafe.analytics.sign_up_free.";
const META_SIGN_UP_STORAGE_PREFIX = "familysafe.analytics.meta_complete_registration.";

type MetaPixelFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: IArguments[];
  push?: MetaPixelFunction;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
  }
}

let analyticsInitialized = false;
let metaPixelInitialized = false;
const trackedSignUps = new Set<string>();
const trackedMetaSignUps = new Set<string>();

export const isProductionAnalyticsHost = (hostname: string): boolean =>
  hostname.toLowerCase() === PRODUCTION_VAULT_HOST;

const pushGtag = (...args: unknown[]) => {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }

  window.gtag = function gtag() {
    // Google expects each queued command to be an Arguments object.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag(...args);
};

/** Loads GA4 only on the live FamilySafe vault. Preview, test and tenant hosts stay untracked. */
export const initializeGoogleAnalytics = (
  hostname = window.location.hostname
): boolean => {
  if (!isProductionAnalyticsHost(hostname) || analyticsInitialized) {
    return false;
  }

  analyticsInitialized = true;
  pushGtag("js", new Date());
  pushGtag("config", GA_MEASUREMENT_ID);

  if (!document.getElementById("familysafe-ga4")) {
    const script = document.createElement("script");
    script.id = "familysafe-ga4";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  return true;
};

const getMetaPixelQueue = (): MetaPixelFunction => {
  if (typeof window.fbq === "function") {
    return window.fbq;
  }

  const fbq = function metaPixelQueue() {
    if (typeof fbq.callMethod === "function") {
      // eslint-disable-next-line prefer-rest-params
      Reflect.apply(fbq.callMethod, window, arguments);
      return;
    }

    // eslint-disable-next-line prefer-rest-params
    fbq.queue?.push(arguments);
  } as MetaPixelFunction;

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  return fbq;
};

/** Loads Meta Pixel only on the live FamilySafe vault. */
export const initializeMetaPixel = (
  hostname = window.location.hostname
): boolean => {
  if (!isProductionAnalyticsHost(hostname) || metaPixelInitialized) {
    return false;
  }

  metaPixelInitialized = true;
  const fbq = getMetaPixelQueue();
  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");

  if (!document.getElementById("familysafe-meta-pixel")) {
    const script = document.createElement("script");
    script.id = "familysafe-meta-pixel";
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  return true;
};

/** Queues one completed-signup event for a newly-created account. */
export const trackFreeSignUp = (
  userId: string | undefined,
  hostname = window.location.hostname,
  storage: Pick<Storage, "getItem" | "setItem"> | null = window.localStorage
): boolean => {
  if (!userId || !isProductionAnalyticsHost(hostname) || typeof window.gtag !== "function") {
    return false;
  }

  const storageKey = `${SIGN_UP_STORAGE_PREFIX}${userId}`;
  if (trackedSignUps.has(storageKey)) {
    return false;
  }

  try {
    if (storage?.getItem(storageKey) === "1") {
      trackedSignUps.add(storageKey);
      return false;
    }
  } catch {
    // The in-memory guard still prevents duplicates during this page load.
  }

  window.gtag("event", SIGN_UP_EVENT, { method: "email" });
  trackedSignUps.add(storageKey);

  try {
    storage?.setItem(storageKey, "1");
  } catch {
    // Tracking must not make a successful registration fail when storage is unavailable.
  }

  return true;
};

/** Queues one Meta completed-registration event for a newly-created account. */
export const trackMetaCompleteRegistration = (
  userId: string | undefined,
  hostname = window.location.hostname,
  storage: Pick<Storage, "getItem" | "setItem"> | null = window.localStorage
): boolean => {
  if (!userId || !isProductionAnalyticsHost(hostname) || typeof window.fbq !== "function") {
    return false;
  }

  const storageKey = `${META_SIGN_UP_STORAGE_PREFIX}${userId}`;
  if (trackedMetaSignUps.has(storageKey)) {
    return false;
  }

  try {
    if (storage?.getItem(storageKey) === "1") {
      trackedMetaSignUps.add(storageKey);
      return false;
    }
  } catch {
    // The in-memory guard still prevents duplicates during this page load.
  }

  window.fbq("track", "CompleteRegistration");
  trackedMetaSignUps.add(storageKey);

  try {
    storage?.setItem(storageKey, "1");
  } catch {
    // Tracking must not make a successful registration fail when storage is unavailable.
  }

  return true;
};
